<template>
  <b-modal
    v-model="localVisible"
    size="md"
    centered
    :no-close-on-backdrop="!answered"
    :no-close-on-esc="!answered"
    :hide-header-close="!answered"
    hide-footer
    :return-focus="returnFocus || undefined"
    title="Envolvidos na ocorrência"
    @hidden="onHidden"
  >
    <p
      class="text-muted mb-3"
      style="font-size: 0.9rem"
    >
      Quantos envolvidos existem nesta ocorrência?
    </p>

    <div class="victims-options">
      <div
        v-for="opt in options"
        :key="opt.value"
        class="victims-option"
        :class="{ 'victims-option--active': status === opt.value }"
        @click="status = opt.value"
      >
        <feather-icon
          :icon="opt.icon"
          size="22"
        />
        <span>{{ opt.label }}</span>
      </div>
    </div>

    <!-- Um envolvido: campos simplificados -->
    <div
      v-if="status === 'single'"
      class="mt-3"
    >
      <div class="row">
        <div class="col-md-6 form-group">
          <label>Nome</label>
          <input
            v-model="person.name"
            type="text"
            class="form-control"
            placeholder="Nome do envolvido (opcional)"
          >
        </div>
        <div class="col-md-3 form-group">
          <label>Idade</label>
          <input
            v-model.number="person.age"
            type="number"
            class="form-control"
            min="0"
            placeholder="Ex: 35"
          >
        </div>
        <div class="col-md-3 form-group">
          <label>Sexo</label>
          <select
            v-model="person.gender"
            class="form-control"
          >
            <option value="">
              Selecione
            </option>
            <option value="M">
              Masculino
            </option>
            <option value="F">
              Feminino
            </option>
            <option value="O">
              Outro
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Múltiplos envolvidos: quantidade -->
    <div
      v-else-if="status === 'multiple'"
      class="mt-3 form-group text-center"
    >
      <label class="d-block">Quantidade de envolvidos</label>
      <div class="victims-stepper mx-auto">
        <button
          type="button"
          class="victims-stepper__btn"
          :disabled="count <= 2"
          @click="decrementCount"
        >
          <feather-icon icon="MinusIcon" size="16" />
        </button>
        <input
          v-model.number="count"
          type="number"
          class="form-control victims-stepper__input"
          min="2"
          @change="onCountInput"
        >
        <button
          type="button"
          class="victims-stepper__btn"
          @click="incrementCount"
        >
          <feather-icon icon="PlusIcon" size="16" />
        </button>
      </div>
      <small class="text-muted d-block mt-2">
        Serão criados registros "Não identificado" para cada envolvido, prontos
        para preenchimento no Atendimento.
      </small>
    </div>

    <div class="text-right mt-4">
      <b-button
        variant="primary"
        :disabled="!canConfirm"
        @click="confirm"
      >
        <feather-icon
          icon="CheckIcon"
          size="14"
          class="mr-50"
        /> Confirmar
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BButton, BModal } from 'bootstrap-vue'

export default {
  name: 'VictimsQuestionModal',

  components: { BButton, BModal },

  props: {
    visible: { type: Boolean, default: false },
    /** Seletor/elemento que recebe o foco ao fechar (padrão do BModal: quem tinha o foco antes). */
    returnFocus: { type: [String, Object], default: null },
    disciplineId: { type: [Number, String], default: null },
    /** Quando true, já existe uma resposta anterior — permite fechar sem responder de novo. */
    answered: { type: Boolean, default: false },
  },

  data() {
    return {
      localVisible: false,
      status: null,
      count: 2,
      person: {
        name: '', age: null, age_unit: 'anos', gender: '',
      },
      roles: [],
      options: [
        { value: 'single', label: 'Um envolvido', icon: 'UserIcon' },
        { value: 'multiple', label: 'Múltiplos envolvidos', icon: 'UsersIcon' },
        { value: 'none', label: 'Sem envolvidos', icon: 'SlashIcon' },
        { value: 'unknown', label: 'Não soube informar', icon: 'HelpCircleIcon' },
      ],
    }
  },

  computed: {
    victimRoleId() {
      const role = this.roles.find(r => r.code === 'vitima')
      return role ? role.id : null
    },

    canConfirm() {
      if (this.status === 'multiple') return Number(this.count) >= 2
      return !!this.status
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.localVisible = val
        if (val) this.reset()
      },
    },
    localVisible(val) {
      if (!val) this.$emit('update:visible', false)
    },
  },

  methods: {
    reset() {
      this.status = null
      this.count = 2
      this.person = {
        name: '', age: null, age_unit: 'anos', gender: '',
      }
      this.loadRoles()
    },

    incrementCount() {
      this.count = Number(this.count || 1) + 1
    },

    decrementCount() {
      this.count = Math.max(2, Number(this.count || 2) - 1)
    },

    onCountInput() {
      if (!this.count || Number(this.count) < 2) this.count = 2
    },

    async loadRoles() {
      try {
        const params = { entity_type: 'person' }
        if (this.disciplineId) params.discipline_id = this.disciplineId
        const res = await this.$http.get(`${this.$store.state.api}involved_roles`, { params })
        this.roles = res.data.data || res.data || []
      } catch (e) {
        this.roles = []
      }
    },

    confirm() {
      if (!this.canConfirm) return

      let payload
      if (this.status === 'single') {
        payload = {
          status: 'informed',
          count: 1,
          involveds: [{
            entity_type: 'person',
            entity: { ...this.person },
            involved_role_id: this.victimRoleId,
          }],
        }
      } else if (this.status === 'multiple') {
        const total = Number(this.count)
        payload = {
          status: 'informed',
          count: total,
          involveds: Array.from({ length: total }, () => ({
            entity_type: 'person',
            entity: { unidentified: true, apparent_description: 'Não identificado' },
            involved_role_id: this.victimRoleId,
          })),
        }
      } else if (this.status === 'none') {
        payload = { status: 'none', count: 0, involveds: [] }
      } else {
        payload = { status: 'unknown', count: null, involveds: [] }
      }

      this.$emit('confirm', payload)
      this.localVisible = false
    },

    onHidden() {
      if (!this.answered) {
        this.$nextTick(() => {
          this.localVisible = true
        })
        return
      }
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.victims-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.victims-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 2px solid #e0ddf5;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  color: #5e5873;
  transition: border-color 0.15s, background 0.15s;
}

.victims-option:hover {
  border-color: #7367f0;
  background: #f8f7ff;
}

.victims-option--active {
  border-color: #7367f0;
  background: #eeecff;
  color: #7367f0;
}

.victims-stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 220px;
}

.victims-stepper__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border: 1.5px solid #e0ddf5;
  border-radius: 50%;
  background: #fff;
  color: #7367f0;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.victims-stepper__btn:hover:not(:disabled) {
  border-color: #7367f0;
  background: #f8f7ff;
}

.victims-stepper__btn:disabled {
  color: #c8c6d8;
  border-color: #eee;
  cursor: not-allowed;
}

.victims-stepper__input {
  text-align: center;
  font-weight: 700;
  font-size: 1.05rem;
  -moz-appearance: textfield;
  appearance: textfield;
}

.victims-stepper__input::-webkit-outer-spin-button,
.victims-stepper__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
